import type { FC } from 'react'
import type React from 'react'
import { useState, useContext } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import EditButton from '@/components/EditButton'
import H6 from '@/components/H6'
import type { Address as AddressLocation } from '@/components/input/AddressSelector'
import AddressSelector from '@/components/input/AddressSelector'
import LoadingSpinner from '@/components/LoadingSpinner'
import Select from '@/components/StyledSelect'
import ctx from '@/context/AppContext'
import { useEditAddressMutation } from '@/lib/apollo'
import logger from '@/lib/vouchLogger'
import type { Address } from '@/types/Activity'
import type { AddressStatus } from '@/types/AddressStatus'

import Confirmation from './Confirmation'
import ConfirmationFooter from './Confirmation/Footer'

interface Props {
  subjectId: string
  customerId: string
  address: Address
}

interface SelectOption<T> {
  label: string
  value: T
}

const toAddressStatusOption = (addressStatus?: string): AddressStatus => {
  switch (addressStatus) {
    case 'Living with family or friends':
      return 'living_with_family_or_friends'
    case 'Short-term rental':
      return 'short_term_rental'
    case 'Home owner':
      return 'home_owner'
    case 'Student accommodation':
      return 'student_accomodation'
    case 'Homeless':
      return 'homeless'
    case 'Employer provided accommodation':
      return 'employer_provided_accomodation'
  }

  return 'renting'
}

const rentingAddressStatusOption: SelectOption<AddressStatus> = {
  label: 'Renting',
  value: 'renting',
}

const getAddressStatusOptions = (
  supportsRenting: boolean,
): SelectOption<AddressStatus>[] => [
  {
    label: 'Living with family or friends',
    value: 'living_with_family_or_friends',
  },
  {
    label: 'Short term rental',
    value: 'short_term_rental',
  },
  {
    label: 'Home owner',
    value: 'home_owner',
  },
  {
    label: 'Student accommodation',
    value: 'student_accomodation',
  },
  {
    label: 'Homeless',
    value: 'homeless',
  },
  {
    label: 'Employer provided accommodation',
    value: 'employer_provided_accomodation',
  },
  ...(supportsRenting ? [rentingAddressStatusOption] : []),
]

const Container = styled.div`
  margin-top: 15px;
  display: grid;
  grid-gap: 15px;
`

const toAddressLocation = (address: Address): AddressLocation => ({
  ...address,
  bedroomIdentifier: address.bedroomIdentifier ?? '',
  name: address.name ?? '',
  equifaxId: address.id ?? '',
  line1: address.line1 ?? '',
  line2: address.line2 ?? '',
  city: address.city ?? '',
  postcode: address.postcode ?? '',
  country: address.country ?? '',
})

const EditAddress: FC<Props> = ({ address, customerId, subjectId }) => {
  const [view, setView] = useState<'EDITING' | 'CONFIRMING'>('EDITING')
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false)
  const [selectedAddressStatus, setSelectedAddressStatus] = useState<AddressStatus>(
    toAddressStatusOption(address?.status),
  )
  const [editedAddress, setEditedAddress] = useState<AddressLocation>(
    toAddressLocation(address),
  )
  const [isLandlordDifferent, setisLandlordDifferent] = useState<boolean>(false)
  const { token } = useContext(ctx)

  const [editAddress, { data, loading, error }] = useEditAddressMutation()

  const { personId } = useContext(ctx)

  const hasLandlord = address.reference !== null

  const handleConfirmation = () => {
    if (address.status === 'Renting' && hasLandlord && view === 'EDITING') {
      setView('CONFIRMING')
      return
    }
    handleSubmit()
  }

  const handleSubmit = () => {
    setView('EDITING')
    editAddress({
      variables: {
        token: token ?? '',
        validatorId: personId ?? '',
        customerId,
        subjectId,
        addressId: address.id ?? '',
        status: selectedAddressStatus,
        bedroomIdentifier: editedAddress.bedroomIdentifier,
        name: editedAddress.name,
        line1: editedAddress.line1,
        line2: editedAddress.line2,
        city: editedAddress.city,
        postcode: editedAddress.postcode,
        country: editedAddress.country,
        equifaxId: editedAddress.equifaxId,
      },
    })
  }

  const supportsRentingAddressType = address.status === 'Renting'

  const handlePanelClose = () => {
    setView('EDITING')
    setisLandlordDifferent(false)
    setIsSidebarOpen(false)
  }

  return (
    <>
      <EditButton onClick={() => setIsSidebarOpen(true)} />
      {isSidebarOpen && (
        <SlideInSidebar
          onClose={handlePanelClose}
          header={<h2>Edit address</h2>}
          content={
            <Container>
              {data?.editAddress === 'Successfully edited address' ? (
                <>
                  The address was successfully updated. Please refresh the page to see the
                  latest changes.
                </>
              ) : loading ? (
                <LoadingSpinner />
              ) : view === 'CONFIRMING' && address.reference ? (
                <Confirmation
                  landlordName={address.reference.name}
                  isLandlordDifferent={isLandlordDifferent}
                />
              ) : (
                <>
                  <div>
                    <H6>Select a new address status</H6>
                    <Select
                      value={selectedAddressStatus}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const newAddressStatus = e.target.value as AddressStatus

                        setSelectedAddressStatus(newAddressStatus)

                        if (newAddressStatus === 'homeless') {
                          setEditedAddress({
                            equifaxId: '',
                            bedroomIdentifier: '',
                            name: '',
                            line1: '',
                            line2: '',
                            city: '',
                            postcode: '',
                            country: '',
                          })
                        }
                      }}
                    >
                      {getAddressStatusOptions(supportsRentingAddressType).map(
                        (addressStatus, i) => (
                          <option value={addressStatus.value} key={i}>
                            {addressStatus.label}
                          </option>
                        ),
                      )}
                    </Select>
                  </div>
                  {selectedAddressStatus !== 'homeless' && (
                    <div>
                      <H6>Update address</H6>
                      <AddressSelector
                        onChangeAddress={setEditedAddress}
                        address={editedAddress}
                      />
                    </div>
                  )}

                  {error?.message && (
                    <div>An unexpected error occurred. {error?.message}</div>
                  )}
                </>
              )}
            </Container>
          }
          footer={
            view === 'CONFIRMING' ? (
              <ConfirmationFooter
                onYes={() => {
                  logger.info(
                    'EditAddress/Confirmation. Ref team confirmed the landlord is the same.',
                    {
                      subjectId,
                      customerId,
                    },
                  )
                  handleSubmit()
                }}
                onNo={() => {
                  logger.info(
                    'EditAddress/Confirmation. Ref team confirmed the landlord is different.',
                    {
                      subjectId,
                      customerId,
                    },
                  )
                  setisLandlordDifferent(true)
                }}
                isLandlordDifferent={isLandlordDifferent}
                onClose={handlePanelClose}
              />
            ) : (
              <>
                <AlternateButton onClick={handlePanelClose}>Cancel</AlternateButton>
                <Button disabled={loading} onClick={handleConfirmation}>
                  Confirm
                </Button>
              </>
            )
          }
        />
      )}
    </>
  )
}

export default EditAddress
